import { Copy, Check } from 'lucide-react'
import { useState } from 'react'

export function GettingStarted() {
  const [copied, setCopied] = useState('')

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopied(id)
    setTimeout(() => setCopied(''), 2000)
  }

  const claudeConfig = `{
  "mcpServers": {
    "tchcash": {
      "command": "tchcash"
    }
  }
}`

  const cursorConfig = `{
  "mcpServers": {
    "tchcash": {
      "command": "tchcash"
    }
  }
}`

  const zcodeConfig = `{
  "tchcash": {
    "command": "tchcash"
  }
}`

  return (
    <section id="getting-started" className="py-24 md:py-32">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-sentient mb-8">
            Getting <span className="text-primary italic">Started</span>
          </h2>
          <p className="text-lg text-foreground/60 leading-relaxed">
            Set up tchcash in minutes and start using paid APIs with your AI agent
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          {/* Step 1 */}
          <div className="border border-foreground/10 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">Install tchcash</h3>
                <p className="text-sm text-foreground/60 mb-4">
                  Install tchcash globally or run it directly with npx
                </p>
                <div className="flex items-center gap-2 bg-foreground/5 rounded p-3 mb-2">
                  <code className="text-sm font-mono text-primary flex-1">npm install -g tchcash</code>
                  <button
                    onClick={() => copyToClipboard('npm install -g tchcash', 'install1')}
                    className="text-foreground/40 hover:text-foreground/60"
                  >
                    {copied === 'install1' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex items-center gap-2 bg-foreground/5 rounded p-3">
                  <code className="text-sm font-mono text-primary flex-1">npx tchcash</code>
                  <button
                    onClick={() => copyToClipboard('npx tchcash', 'install2')}
                    className="text-foreground/40 hover:text-foreground/60"
                  >
                    {copied === 'install2' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="border border-foreground/10 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">Configure MCP Client</h3>
                <p className="text-sm text-foreground/60 mb-4">
                  Add tchcash to your MCP client configuration
                </p>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold mb-2 text-foreground/80">Claude Desktop</h4>
                    <div className="flex items-start gap-2 bg-foreground/5 rounded p-3">
                      <code className="text-xs font-mono text-foreground/80 flex-1 whitespace-pre">{claudeConfig}</code>
                      <button
                        onClick={() => copyToClipboard(claudeConfig, 'claude')}
                        className="text-foreground/40 hover:text-foreground/60 flex-shrink-0"
                      >
                        {copied === 'claude' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold mb-2 text-foreground/80">Cursor</h4>
                    <div className="flex items-start gap-2 bg-foreground/5 rounded p-3">
                      <code className="text-xs font-mono text-foreground/80 flex-1 whitespace-pre">{cursorConfig}</code>
                      <button
                        onClick={() => copyToClipboard(cursorConfig, 'cursor')}
                        className="text-foreground/40 hover:text-foreground/60 flex-shrink-0"
                      >
                        {copied === 'cursor' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold mb-2 text-foreground/80">ZCode</h4>
                    <div className="flex items-start gap-2 bg-foreground/5 rounded p-3">
                      <code className="text-xs font-mono text-foreground/80 flex-1 whitespace-pre">{zcodeConfig}</code>
                      <button
                        onClick={() => copyToClipboard(zcodeConfig, 'zcode')}
                        className="text-foreground/40 hover:text-foreground/60 flex-shrink-0"
                      >
                        {copied === 'zcode' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="border border-foreground/10 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">Setup Wallet</h3>
                <p className="text-sm text-foreground/60 mb-4">
                  Call the setup_wallet tool to create your wallet
                </p>
                <div className="bg-foreground/5 rounded p-4">
                  <code className="text-xs font-mono text-foreground/80 block">
                    {"{\"name\": \"setup_wallet\", \"arguments\": {}}"}
                  </code>
                </div>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="border border-foreground/10 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                4
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">Fund Wallet</h3>
                <p className="text-sm text-foreground/60 mb-4">
                  Transfer TCH to your wallet address (recommended: 10 TCH)
                </p>
                <div className="bg-primary/10 border border-primary/20 rounded p-4">
                  <p className="text-sm font-mono text-primary">
                    💡 Tell your user: "Please transfer 10 TCH to the wallet address shown by setup_wallet"
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 5 */}
          <div className="border border-foreground/10 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                5
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold mb-2">Start Using APIs</h3>
                <p className="text-sm text-foreground/60 mb-4">
                  Call paid APIs with automatic payment handling
                </p>
                <div className="bg-foreground/5 rounded p-4">
                  <code className="text-xs font-mono text-foreground/80 block">
                    {"{\"name\": \"fetch_market_data\", \"arguments\": {}}"}
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
